import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Drawer, IconButton, ThemeProvider, Typography } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { CookieConsent } from './sections/CookieConsent';
import TermsOfService from '../TermsOfService';
import PrivacyPolicy from '../PrivacyPolicy';
import { initClarityIfConsented } from '../../config/clarity';
import { SwedishHome } from './public/SwedishHome';
import { EnglishHome } from './public/EnglishHome';
import { SupportingPage } from './public/SupportingPages';
import { EnglishSupportingPage } from './public/EnglishSupportingPages';
import { BuildingDemo } from './public/BuildingDemo';
import { BuildingTeaser } from './public/BuildingTeaser';
import { isLegacyBuildingDemo } from './public/buildingTeaserData';
import { ExampleReport } from './public/ExampleReport';
import { homeMetadata, publicSeoUrl } from './public/seo';
import { articleCrackImages } from './public/articleImages';
import { Action, Chapter, colors, contactPhone, ContactBand, fonts, Frame, homeContactLabel, Intro, publicTheme, Wordmark } from './public/design';
import {
  equivalentPublicUrl, getPublicRoute, navigatePublic, normalizePath, publicPath,
  publicRouteDefinitions, PublicLocale, PublicRouteId, shouldHandleLink,
} from './public/routes';

const primaryLinks: PublicRouteId[] = ['about', 'process', 'buildingPass', 'services'];
const headerLinks: PublicRouteId[] = [...primaryLinks, 'insights'];
const trustLinks: PublicRouteId[] = ['insights', 'documentation', 'forManagers', 'technology'];
const descriptions: Record<PublicLocale, string> = {
  sv: 'Se vad som förändras på fasader, tak och exteriöra ytor. KlaraView hjälper fastighetsägare att prioritera underhåll med bilddata och mänsklig bedömning.',
  en: 'See what changes across façades, roofs and exterior surfaces. KlaraView helps property owners prioritise maintenance using image documentation and professional assessment.',
};
interface ArticleMetadata {
  seoTitle?: string;
  description: string;
  image: string;
  published?: string;
  modified?: string;
}

const articleMetadata: Partial<Record<PublicRouteId, Record<PublicLocale, ArticleMetadata>>> = {
  expensiveFacadeDamage: {
    sv: {
    description: 'Små fasadskador kan växa när de inte upptäcks i tid. Marko Rajkovic skriver om tidig upptäckt, byggnadshistorik och bättre underhållsbeslut.',
    image: articleCrackImages.preview,
    published: '2026-09-16',
    modified: '2026-09-16',
  },
    en: {
    description: 'Small façade defects can grow when they are not discovered in time. Marko Rajkovic discusses early discovery, building history and better maintenance decisions.',
    image: articleCrackImages.preview,
    published: '2026-09-16',
    modified: '2026-09-16',
  },
  },
  facadeBudget: {
    sv: {
      seoTitle: 'Fasaddata: bättre underhåll och budget',
      description: 'Gör fasader och tak till beslutsunderlag. Så kan strukturerad fastighetsdata stödja underhåll, prioriteringar och nästa års budget – med AI och mänsklig expertis.',
      image: '/images/insikter/budget-facade-illustration.jpg',
      published: '2026-10-07',
      modified: '2026-10-07',
    },
    en: {
      seoTitle: 'Façade data: better maintenance and budgeting',
      description: 'Turn façades and roofs into evidence for decisions. How structured property data can support maintenance, priorities and next year’s budget—with AI and human expertise.',
      image: '/images/insikter/budget-facade-illustration.jpg',
      published: '2026-10-07',
      modified: '2026-10-07',
    },
  },
  newProperty: {
    sv: {
      description: 'Vad bygger vår trygghet på? Hur en byggnad ser ut – eller vad vi faktiskt vet om dess skick?',
      image: '/images/insikter/novi-sad-station.jpg',
      published: '2026-09-29',
      modified: '2026-09-29',
    },
    en: {
      description: 'What is our sense of security based on? How a building looks—or what we actually know about its condition?',
      image: '/images/insikter/novi-sad-station.jpg',
      published: '2026-09-29',
      modified: '2026-09-29',
    },
  },
};
const locationKey = () => `${window.location.pathname}${window.location.search}${window.location.hash}`;
const legalAppearance = {
  primary: colors.rust, secondary: colors.green, text: colors.ink,
  textSecondary: colors.muted, background: colors.paper, logo: <Wordmark />,
};

const shellCopy = {
  sv: {
    notFoundTitle: 'Sidan hittades inte',
    notFoundLabel: '404 / SIDAN HITTADES INTE',
    notFoundHeading: 'Här finns ingen sida.',
    notFoundText: 'Länken kan vara gammal. Gå till startsidan eller kontakta oss så hjälper vi dig vidare.',
    home: 'Till startsidan',
    skip: 'Hoppa till innehållet',
    strapline: 'En fastighet i taget. Ett tydligare underlag.',
    homeAria: 'KlaraView – startsida',
    mainNav: 'Huvudmeny',
    mobileNav: 'Mobilmeny',
    login: 'Logga in',
    switchLanguage: 'Read this page in English',
    switchLabel: 'EN',
    contact: 'Kontakta oss',
    openMenu: 'Öppna menyn',
    closeMenu: 'Stäng menyn',
    explore: 'Utforska',
    trust: 'Förtroende',
    about: 'Om oss',
    faq: 'Frågor & svar',
    question: 'Vad vill du få kontroll över?',
    tagline: <>Se vad som förändras.<br />Prioritera rätt.</>,
    city: 'Malmö, Sverige',
    demoNote: 'Produktdemonstrationer visar exempeldata.',
    buildingLabel: 'FASTIGHETENS MINNE / BYGGNADSPASSET',
    buildingTitle: 'Vad har hänt?\nVad har förändrats?',
    buildingLead: 'Vilka skador har åtgärdats? Var finns bilderna? Prova att byta år, filtrera och välja en observation i vårt illustrativa byggnadspass.',
    teaserTitle: 'Hitta platsen.\nSe detaljen.',
    teaserLead: 'En byggnad, flera perspektiv. Välj en plats på hörnhuset och öppna närbilden. Se hur bild, observation och ett möjligt nästa steg kan hållas ihop i byggnadspasset.',
  },
  en: {
    notFoundTitle: 'Page not found',
    notFoundLabel: '404 / PAGE NOT FOUND',
    notFoundHeading: 'There is no page here.',
    notFoundText: 'The link may be out of date. Go to the home page or contact us and we will help you.',
    home: 'Go to the home page',
    skip: 'Skip to content',
    strapline: 'One property at a time. A clearer basis for decisions.',
    homeAria: 'KlaraView – home page',
    mainNav: 'Main navigation',
    mobileNav: 'Mobile navigation',
    login: 'Sign in',
    switchLanguage: 'Läs den här sidan på svenska',
    switchLabel: 'SV',
    contact: 'Contact us',
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
    explore: 'Explore',
    trust: 'Trust',
    about: 'About us',
    faq: 'Questions & answers',
    question: 'What would you like to understand?',
    tagline: <>See what changes.<br />Prioritise what matters.</>,
    city: 'Malmö, Sweden',
    demoNote: 'Product demonstrations use sample data.',
    buildingLabel: 'THE PROPERTY MEMORY / BUILDING PASS',
    buildingTitle: 'What happened?\nWhat changed?',
    buildingLead: 'Which defects were resolved? Where are the images? Change year, filter and select an observation in our illustrative building pass.',
    teaserTitle: 'Find the location.\nSee the detail.',
    teaserLead: 'One building, several perspectives. Select a location on the corner building and open the close-up. See how photos, observations and a possible next step can stay connected in the building pass.',
  },
};

export function MarketingShell({ onLogin }: { onLogin: () => void }) {
  const [location, setLocation] = useState(locationKey);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const mainRef = useRef<HTMLElement>(null);
  const firstVisit = useRef(true);
  const url = new URL(location, window.location.origin);
  const path = normalizePath(url.pathname);
  const route = getPublicRoute(path);
  const locale: PublicLocale = route?.locale || (path.startsWith('/en') ? 'en' : 'sv');
  const text = shellCopy[locale];
  const routeId = route?.routeId;
  const contactLabel = routeId === 'home' ? homeContactLabel[locale] : text.contact;
  const printable = routeId === 'exampleReport';
  const article = routeId ? articleMetadata[routeId]?.[locale] : undefined;
  const pageTitle = `${article?.seoTitle || route?.title || text.notFoundTitle} | KlaraView`;
  const description = routeId === 'home'
    ? homeMetadata[locale].description
    : article?.description || route?.description || descriptions[locale];

  useEffect(() => {
    const update = () => { setLocation(locationKey()); setDrawerOpen(false); };
    window.addEventListener('popstate', update);
    window.addEventListener('hashchange', update);
    if (window.location.pathname === '/' && window.localStorage.getItem('klaraview.lang') === 'en') {
      const destination = `${publicPath('home', 'en')}${window.location.search}${window.location.hash}`;
      window.history.replaceState(window.history.state, '', destination);
      update();
    }
    initClarityIfConsented();
    return () => { window.removeEventListener('popstate', update); window.removeEventListener('hashchange', update); };
  }, []);

  useEffect(() => {
    window.localStorage.setItem('klaraview.lang', locale);
  }, [locale]);

  useEffect(() => {
    const oldTitle = document.title;
    const oldLang = document.documentElement.lang;
    const ensureMeta = (selector: string, attributes: Record<string, string>) => {
      const existing = document.querySelector(selector);
      if (existing) return { element: existing, created: false };
      const element = document.createElement('meta');
      Object.entries(attributes).forEach(([name, value]) => element.setAttribute(name, value));
      document.head.appendChild(element);
      return { element, created: true };
    };
    const metadata = [
      { selector: 'meta[name="description"]', value: description },
      { selector: 'meta[property="og:title"]', value: pageTitle },
      { selector: 'meta[property="og:description"]', value: description },
    ].map(item => {
      const attributes: Record<string, string> = item.selector.includes('name=')
        ? { name: 'description' }
        : { property: item.selector.match(/property="([^"]+)"/)?.[1] || '' };
      const ensured = ensureMeta(item.selector, attributes);
      return { ...item, element: ensured.element, created: ensured.created, previous: ensured.element.getAttribute('content') || '' };
    });
    const robots = route ? undefined : ensureMeta('meta[name="robots"]', { name: 'robots' });
    const previousRobotsContent = robots?.element.getAttribute('content') ?? null;
    robots?.element.setAttribute('content', 'noindex, follow');
    const suspendedLinks = route ? [] : Array.from(
      document.head.querySelectorAll<HTMLLinkElement>('link[rel="canonical"], link[rel="alternate"][hreflang]'),
      element => ({ element, nextSibling: element.nextSibling }),
    );
    suspendedLinks.forEach(({ element }) => element.remove());

    const canonicalUrl = publicSeoUrl(path);
    const linkValues: ReadonlyArray<readonly ['canonical' | 'alternate-sv' | 'alternate-en', string]> = route ? [
      ['canonical', canonicalUrl],
      ['alternate-sv', publicSeoUrl(publicPath(route.routeId, 'sv'))],
      ['alternate-en', publicSeoUrl(publicPath(route.routeId, 'en'))],
    ] : [];
    const links = linkValues.map(([key, href]) => {
      const selector = key === 'canonical' ? 'link[rel="canonical"]' : `link[data-klaraview-${key}]`;
      const existing = document.querySelector(selector) as HTMLLinkElement | null;
      const element = existing || document.head.appendChild(document.createElement('link'));
      const previous = { href: element.getAttribute('href') || '', rel: element.getAttribute('rel') || '', hreflang: element.getAttribute('hreflang') || '' };
      element.rel = key === 'canonical' ? 'canonical' : 'alternate';
      element.href = href;
      if (key !== 'canonical') {
        element.hreflang = key === 'alternate-sv' ? 'sv-SE' : 'en';
        element.setAttribute(`data-klaraview-${key}`, 'true');
      }
      return { element, created: !existing, previous };
    });
    const ogTypeMeta = ensureMeta('meta[property="og:type"]', { property: 'og:type' });
    const previousOgType = ogTypeMeta.element.getAttribute('content') || '';
    ogTypeMeta.element.setAttribute('content', article ? 'article' : 'website');
    const ogImageMeta = ensureMeta('meta[property="og:image"]', { property: 'og:image' });
    const previousOgImage = ogImageMeta.element.getAttribute('content') || '';
    if (article) ogImageMeta.element.setAttribute('content', publicSeoUrl(article.image));
    else ogImageMeta.element.removeAttribute('content');
    const structuredData = document.querySelector('script[data-klaraview-structured-data]') || (() => {
      const element = document.createElement('script');
      element.type = 'application/ld+json';
      element.setAttribute('data-klaraview-structured-data', 'true');
      return document.head.appendChild(element);
    })();
    const previousStructuredData = structuredData.textContent || '';
    structuredData.textContent = article ? JSON.stringify({
      '@context': 'https://schema.org', '@type': 'BlogPosting', headline: route?.title,
      description: article.description, image: [publicSeoUrl(article.image)],
      ...(article.published ? { datePublished: article.published } : {}),
      ...(article.modified ? { dateModified: article.modified } : {}),
      inLanguage: locale === 'sv' ? 'sv-SE' : 'en',
      author: { '@type': 'Person', name: 'Marko Rajkovic', jobTitle: 'Co-Founder & CCO', worksFor: { '@type': 'Organization', name: 'KlaraView' } },
      publisher: { '@type': 'Organization', name: 'KlaraView' },
      mainEntityOfPage: { '@type': 'WebPage', '@id': canonicalUrl },
    }) : '';
    document.title = pageTitle;
    document.documentElement.lang = locale === 'sv' ? 'sv' : 'en';
    metadata.forEach(item => item.element.setAttribute('content', item.value));
    return () => {
      document.title = oldTitle;
      document.documentElement.lang = oldLang;
      metadata.forEach(item => item.created ? item.element.remove() : item.element.setAttribute('content', item.previous));
      links.forEach(item => {
        if (item.created) item.element.remove();
        else {
          item.element.setAttribute('href', item.previous.href);
          item.element.setAttribute('rel', item.previous.rel);
          if (item.previous.hreflang) item.element.setAttribute('hreflang', item.previous.hreflang);
          else item.element.removeAttribute('hreflang');
        }
      });
      if (robots) {
        if (robots.created) robots.element.remove();
        else if (previousRobotsContent === null) robots.element.removeAttribute('content');
        else robots.element.setAttribute('content', previousRobotsContent);
      }
      [...suspendedLinks].reverse().forEach(({ element, nextSibling }) => {
        document.head.insertBefore(element, nextSibling?.parentNode === document.head ? nextSibling : null);
      });
      if (ogTypeMeta.created) ogTypeMeta.element.remove(); else ogTypeMeta.element.setAttribute('content', previousOgType);
      if (ogImageMeta.created) ogImageMeta.element.remove(); else if (previousOgImage) ogImageMeta.element.setAttribute('content', previousOgImage); else ogImageMeta.element.removeAttribute('content');
      if (structuredData.parentElement) structuredData.textContent = previousStructuredData;
    };
  }, [article, description, locale, pageTitle, path, route]);

  useEffect(() => {
    const hash = new URL(location, window.location.origin).hash.slice(1);
    const preservedScroll = window.history.state?.klaraviewScrollY;
    if (typeof preservedScroll === 'number') {
      window.history.replaceState({ ...window.history.state, klaraviewScrollY: undefined }, '', location);
      window.requestAnimationFrame(() => window.scrollTo({ top: preservedScroll, behavior: 'auto' }));
    } else {
      const target = hash ? document.getElementById(hash) : null;
      if (target) {
        target.scrollIntoView({ behavior: 'auto' });
        if (hash === 'main') target.focus({ preventScroll: true });
      } else {
        window.scrollTo({ top: 0, behavior: 'auto' });
        if (!firstVisit.current) mainRef.current?.focus({ preventScroll: true });
      }
    }
    firstVisit.current = false;
  }, [location]);

  const targetLocale: PublicLocale = locale === 'sv' ? 'en' : 'sv';
  const languageHref = equivalentPublicUrl(location, targetLocale);
  const switchLanguage = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (!shouldHandleLink(event, event.currentTarget)) return;
    event.preventDefault();
    window.localStorage.setItem('klaraview.lang', targetLocale);
    navigatePublic(languageHref, { preserveScroll: true });
  };

  const link = (routeId: PublicRouteId) => {
    const href = publicPath(routeId, locale);
    return <Box component="a" key={routeId} href={href} aria-current={path === href ? 'page' : undefined}
      sx={{ color: 'inherit', textDecoration: 'none', fontSize: 14, py: 1.2, display: 'block', borderBottom: path === href ? `2px solid ${colors.rust}` : '2px solid transparent', '&:hover': { textDecoration: 'underline' } }}>{routeId === 'about' ? text.about : routeId === 'contact' ? contactLabel : publicRouteDefinitions[routeId].titles[locale]}</Box>;
  };

  let page: React.ReactNode;
  if (routeId === 'home') page = locale === 'sv' ? <SwedishHome search={url.search} /> : <EnglishHome search={url.search} />;
  else if (routeId === 'buildingPass') page = <>
    <Intro label={text.buildingLabel} title={isLegacyBuildingDemo(url.search) ? text.buildingTitle : text.teaserTitle}>
      {isLegacyBuildingDemo(url.search) ? text.buildingLead : text.teaserLead}
    </Intro>
    <Chapter>{isLegacyBuildingDemo(url.search)
      ? <BuildingDemo key={url.search} search={url.search} locale={locale} />
      : <BuildingTeaser search={url.search} locale={locale} />}</Chapter><ContactBand locale={locale} />
  </>;
  else if (routeId === 'exampleReport') page = <ExampleReport search={url.search} locale={locale} />;
  else if (routeId === 'privacy') page = <PrivacyPolicy language={locale} appearance={legalAppearance} onBack={() => navigatePublic(publicPath('home', locale))} />;
  else if (routeId === 'terms') page = <TermsOfService language={locale} appearance={legalAppearance} onBack={() => navigatePublic(publicPath('home', locale))} />;
  else if (route && locale === 'sv') page = <SupportingPage key={path + url.search} path={path} search={url.search} />;
  else if (route) page = <EnglishSupportingPage key={path + url.search} routeId={route.routeId} search={url.search} />;
  else page = <><Intro label={text.notFoundLabel} title={text.notFoundHeading}>{text.notFoundText}</Intro><Chapter><Action href={publicPath('home', locale)}>{text.home}</Action></Chapter></>;

  return <ThemeProvider theme={publicTheme}>
    <Box onClick={event => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const anchor = target.closest('a');
      if (!(anchor instanceof HTMLAnchorElement) || !shouldHandleLink(event, anchor)) return;
      event.preventDefault();
      navigatePublic(`${anchor.pathname}${anchor.search}${anchor.hash}`);
    }} sx={{ minHeight: '100vh', bgcolor: colors.paper, color: colors.ink, fontFamily: fonts.sans,
      '& a:focus-visible': { outline: `3px solid ${colors.rust}`, outlineOffset: 4 },
      '& main:focus': { outline: 'none' },
      '@media print': { bgcolor: 'white', '& [data-no-print]': { display: 'none !important' } },
      '@media (prefers-reduced-motion: reduce)': { '& *, & *::before, & *::after': { transition: 'none !important', animation: 'none !important', scrollBehavior: 'auto !important' } },
    }}>
      <Box component="a" href="#main" sx={{ position: 'fixed', top: -100, left: 16, zIndex: 1600, bgcolor: 'white', color: colors.ink, p: 2, '&:focus': { top: 12 } }}>{text.skip}</Box>
      {!printable && <Box data-no-print sx={{ bgcolor: colors.graphite, color: colors.white, py: .6 }}><Frame><Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', columnGap: 2, flexWrap: 'wrap', fontSize: 11 }}><Typography sx={{ fontSize: 'inherit' }}>{text.strapline}</Typography><Box component="a" href={contactPhone.href} sx={{ color: 'inherit', textDecoration: 'none', py: .5, '&:hover': { textDecoration: 'underline' } }}>{contactPhone.label}</Box></Box></Frame></Box>}
      {!printable && <Box component="header" data-no-print sx={{ position: 'sticky', top: 0, zIndex: 1100, bgcolor: 'rgba(242,243,239,.97)', borderBottom: `1px solid ${colors.line}` }}>
        <Frame><Box sx={{ display: 'flex', minHeight: 80, alignItems: 'center', gap: { xs: 1, lg: 3 } }}>
          <Box component="a" href={publicPath('home', locale)} aria-label={text.homeAria} sx={{ color: 'inherit', textDecoration: 'none', mr: 'auto' }}><Wordmark /></Box>
          <Box component="nav" aria-label={text.mainNav} sx={{ display: { xs: 'none', lg: 'flex' }, gap: 2.5 }}>{headerLinks.map(link)}</Box>
          <Button onClick={onLogin} sx={{ display: { xs: 'none', sm: 'inline-flex' }, color: colors.muted }}>{text.login}</Button>
          <Button href={languageHref} onClick={switchLanguage} lang={targetLocale} hrefLang={targetLocale === 'sv' ? 'sv-SE' : 'en'} sx={{ color: colors.muted, px: 1, minWidth: 44 }} aria-label={text.switchLanguage}>{text.switchLabel}</Button>
          <Box sx={{ display: { xs: 'none', lg: 'block' } }}><Action href={publicPath('contact', locale)}>{contactLabel}</Action></Box>
          <IconButton aria-label={text.openMenu} onClick={() => setDrawerOpen(true)} sx={{ display: { lg: 'none' } }}><MenuIcon /></IconButton>
        </Box></Frame>
      </Box>}
      <Drawer open={drawerOpen} anchor="right" onClose={() => setDrawerOpen(false)}>
        <Box sx={{ width: 'min(340px, 90vw)', p: 3 }} onClick={event => {
          const target = event.target;
          const anchor = target instanceof Element ? target.closest('a') : null;
          if (anchor instanceof HTMLAnchorElement && shouldHandleLink(event, anchor)) {
            event.preventDefault(); navigatePublic(anchor.getAttribute('href') || publicPath('home', locale)); setDrawerOpen(false);
          }
        }}>
          <IconButton aria-label={text.closeMenu} onClick={() => setDrawerOpen(false)} sx={{ display: 'flex', ml: 'auto' }}><CloseIcon /></IconButton>
          <Box component="nav" aria-label={text.mobileNav}>{[...primaryLinks, ...trustLinks, 'contact' as PublicRouteId].map(link)}</Box>
          <Button onClick={() => { setDrawerOpen(false); onLogin(); }}>{text.login}</Button>
        </Box>
      </Drawer>
      <Box component="main" id="main" ref={mainRef} tabIndex={-1}
        key={routeId === 'buildingPass' && !isLegacyBuildingDemo(url.search) ? path : path + url.search}>{page}</Box>
      {!printable && <Box component="footer" data-no-print sx={{ bgcolor: colors.graphite, color: 'white', py: 7, borderTop: '1px solid #596157' }}>
        <Frame><Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: '1.3fr 1fr 1.3fr 1.2fr' }, gap: 4 }}>
          <Box sx={{ gridColumn: { xs: '1 / -1', md: 'auto' } }}><Box component="a" href={publicPath('home', locale)} aria-label={text.homeAria} sx={{ color: 'inherit', textDecoration: 'none' }}><Wordmark dark /></Box><Typography sx={{ my: 3, color: '#CFD5CC' }}>{text.tagline}</Typography><Typography sx={{ fontSize: 13 }}>{text.city}</Typography></Box>
          <Box><Typography sx={{ mb: 2, fontWeight: 700 }}>{text.explore}</Typography>{primaryLinks.map(link)}</Box>
          <Box><Typography sx={{ mb: 2, fontWeight: 700 }}>{text.trust}</Typography>{trustLinks.map(link)}
            <Box component="a" href={`${publicPath('process', locale)}#fragor`} sx={{ display: 'block', py: 1.2, color: 'inherit', fontSize: 14 }}>{text.faq}</Box>
          </Box>
          <Box sx={{ gridColumn: { xs: '1 / -1', md: 'auto' } }}><Typography sx={{ mb: 2, fontWeight: 700 }}>{text.question}</Typography><Action href={publicPath('contact', locale)}>{contactLabel}</Action><Box component="a" href={contactPhone.href} sx={{ display: 'block', color: 'white', mt: 3, py: 1 }}>{contactPhone.label}</Box><Box component="a" href="mailto:info@klaraview.se" sx={{ display: 'block', color: 'white', mb: 3, py: 1 }}>info@klaraview.se</Box><Box component="a" href="https://www.linkedin.com/company/klaraview" rel="noopener noreferrer" target="_blank" sx={{ color: 'white' }}>LinkedIn ↗</Box></Box>
        </Box><Box sx={{ borderTop: '1px solid #596157', mt: 5, pt: 3, display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}><Typography sx={{ mr: 'auto', fontSize: 12 }}>© {new Date().getFullYear()} KlaraView AB</Typography>{link('privacy')}{link('terms')}<Typography sx={{ fontSize: 12 }}>{text.demoNote}</Typography></Box></Frame>
      </Box>}
      {!printable && <Box data-no-print><CookieConsent onOpenPrivacy={() => navigatePublic(publicPath('privacy', locale))} appearance="public" language={locale} /></Box>}
    </Box>
  </ThemeProvider>;
}
